import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const About = () => {
	return (
		<div className="min-h-screen bg-gray-50">
			<header className="bg-white shadow-sm border-b border-gray-200">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex items-center justify-between">
						<Link to="/">
							<Logo showText={true} width={50} height={50} />
						</Link>
						<Link
							to="/"
							className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
						>
							Back to Home
						</Link>
					</div>
				</div>
			</header>

			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				<div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
					<h1 className="text-4xl font-bold text-gray-900 mb-4">About iVault</h1>
					<p className="text-gray-600 mb-8 text-lg">
						Your secure digital vault for document management and investor relations. We help startups close rounds faster and investors evaluate deals with confidence.
					</p>

					<div className="prose prose-lg max-w-none">
						<section className="mb-10">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Mission</h2>
							<p className="text-gray-700 mb-4">
								iVault exists to make fundraising and due diligence secure, transparent, and efficient. We connect startups with investors through a single platform for document sharing, pitch events, and investor networks—so founders can share the right information with the right people while keeping sensitive data protected.
							</p>
							<p className="text-gray-700">
								We believe that better tools for data rooms and investor relations lead to better outcomes for both sides of the table: founders close rounds with less friction, and investors get the clarity they need to make informed decisions.
							</p>
						</section>

						<section className="mb-10">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Story</h2>
							<p className="text-gray-700 mb-4">
								iVault was built by Techpuram to solve a problem we saw repeatedly: startups sharing pitch decks and financials over email and generic cloud folders, with no control over who saw what, when, or for how long. Investors, meanwhile, were juggling multiple links and chasing founders for updated numbers.
							</p>
							<p className="text-gray-700">
								We created iVault to bring data rooms, NDA gating, e-signatures, and real-time engagement analytics into one place—so both founders and investors can focus on the deal, not the paperwork.
							</p>
						</section>

						<section className="mb-10">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">What We Do</h2>
							<p className="text-gray-700 mb-4">
								Our platform gives you everything needed to run a professional data room and investor relations process:
							</p>
							<ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
								<li><strong>Secure document storage and sharing</strong> — Upload pitch decks, financials, and due diligence materials with granular permissions and instant revocation.</li>
								<li><strong>Investor access and analytics</strong> — See who viewed which documents, for how long, and manage access by file, folder, or investor group.</li>
								<li><strong>Pitch event organization</strong> — Run and participate in pitch events, manage deal flow, and connect with startups and networks in one place.</li>
								<li><strong>Investor network management</strong> — Angel networks and VC funds can manage members, share deal rooms, and track commitments and syndication.</li>
								<li><strong>NDA and e-signature</strong> — Gate your room behind a custom NDA and collect legally binding e-signatures before any document is revealed.</li>
								<li><strong>Subscription and billing</strong> — Clear plans for startups, investors, and networks with storage, investor links, and add-ons that scale with you.</li>
							</ul>
						</section>

						<section className="mb-10">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">Who We Serve</h2>
							<p className="text-gray-700 mb-4">
								iVault is built for founders raising capital, angels and VCs evaluating deals, and investor networks managing deal flow at scale. Whether you're running a single data room for a seed round or coordinating multiple rooms and events across a fund, we provide the security, control, and visibility you need.
							</p>
						</section>

						<section className="mb-10">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Values</h2>
							<ul className="list-disc pl-6 text-gray-700 space-y-2">
								<li><strong>Security first</strong> — Your documents and data are protected with enterprise-grade encryption, access controls, and audit trails.</li>
								<li><strong>Transparency</strong> — Clear pricing, clear permissions, and clear visibility into who accessed what and when.</li>
								<li><strong>Simplicity</strong> — Set up in minutes, not days. No complex onboarding or hidden workflows.</li>
							</ul>
						</section>

						<section className="mb-8">
							<h2 className="text-2xl font-semibold text-gray-900 mb-4">Get in Touch</h2>
							<p className="text-gray-700">
								Have questions about iVault or want to see how we can help your raise or fund? <Link to="/contact" className="text-blue-600 hover:underline">Contact us</Link>—we’re happy to help.
							</p>
						</section>
					</div>
				</div>
			</div>
		</div>
	);
};

export default About;
